/** Internal: touch + gestures (swipe, tap, pinch). Use InputManager only. */

import type { TouchState, TouchPoint, Vector2Like, SwipeDirection, SwipeData } from "./Input_Types";

export class TouchGestureManager {
    // Touch state
    private touchState: TouchState = {
        activeTouches: new Map(),
        touchCount: 0,
        isActive: false,
    };

    // Tap detection
    private touchStartTime: number = 0;
    private lastTapTime: number = 0;
    private touchStartPosition: Vector2Like | null = null;
    private tapDetected: boolean = false;
    private readonly TAP_DISTANCE_THRESHOLD = 20; // Max distance in pixels to be considered a tap
    private readonly TAP_TIME_THRESHOLD = 300; // Max time in ms to be considered a tap

    // Touch delta tracking
    private lastTouchPosition: Vector2Like | null = null;

    // Pinch tracking
    private lastPinchDistance: number = 0;
    private currentPinchDistance: number = 0;
    private pinchDelta: number = 0;

    // Swipe/gesture detection
    private swipeStart: Vector2Like | null = null;
    private swipeEnd: Vector2Like | null = null;
    private swipeStartTime: number = 0;
    private swipeThreshold: number = 50;
    private currentSwipe: SwipeDirection | null = null;
    private swipeDistance: number = 0;
    private currentSwipeData: SwipeData | null = null;
    private isActive: boolean = false;
    private swipeLoopId: number | null = null;
    private stopTimer: number | null = null;
    private hasStopped: boolean = false;

    /** Call every frame. */
    update(): void {
        // Calculate pinch delta for two-finger gestures
        if (this.touchState.touchCount === 2) {
            this.currentPinchDistance = this.calculatePinchDistance();

            if (this.lastPinchDistance === 0) {
                this.lastPinchDistance = this.currentPinchDistance;
                this.pinchDelta = 0;
            } else {
                this.pinchDelta = this.currentPinchDistance - this.lastPinchDistance;
                this.lastPinchDistance = this.currentPinchDistance;
            }
        } else {
            this.lastPinchDistance = 0;
            this.currentPinchDistance = 0;
            this.pinchDelta = 0;
        }

        // Reset touch position tracking when no touches
        if (!this.touchState.isActive) {
            this.lastTouchPosition = null;
        }
    }

    // ===== TOUCH METHODS =====

    startTouch(touches: TouchPoint[]): void {
        this.touchState.isActive = true;
        this.touchStartTime = performance.now();
        
        // Record starting position for distance-based tap detection (single touch only)
        if (touches.length === 1) {
            this.touchStartPosition = { ...touches[0].position.canvas };
            this.tapDetected = false; // Reset tap flag
        } else {
            this.touchStartPosition = null;
        }

        this.touchState.activeTouches.clear();
        touches.forEach(touch => {
            this.touchState.activeTouches.set(touch.id, touch);
        });

        this.touchState.touchCount = touches.length;
    }

    updateTouchPositions(touches: TouchPoint[]): void {
        touches.forEach(touch => {
            if (this.touchState.activeTouches.has(touch.id)) {
                this.touchState.activeTouches.set(touch.id, touch);
            }
        });

        this.touchState.touchCount = this.touchState.activeTouches.size;
    }

    endTouch(finalPosition?: Vector2Like): void {
        // Check for distance-based tap detection
        if (this.touchStartPosition && finalPosition) {
            const dx = finalPosition.x - this.touchStartPosition.x;
            const dy = finalPosition.y - this.touchStartPosition.y;
            const distance = Math.sqrt(dx * dx + dy * dy);
            
            const now = performance.now();
            const touchDuration = now - this.touchStartTime;
            const timeSinceLastTap = now - this.lastTapTime;
            
            // Valid tap: short distance, short duration, and not too soon after last tap
            if (distance < this.TAP_DISTANCE_THRESHOLD && 
                touchDuration < this.TAP_TIME_THRESHOLD &&
                timeSinceLastTap >= 100) {
                this.tapDetected = true;
                this.lastTapTime = now;
            }
        }
        
        // If position provided, finalize gesture
        if (finalPosition) {
            this.endSwipe(finalPosition);
        }

        this.touchState.isActive = false;
        this.touchState.activeTouches.clear();
        this.touchState.touchCount = 0;
        this.touchStartPosition = null;
    }

    isTouchActive(): boolean {
        return this.touchState.isActive;
    }

    getTouchCount(): number {
        return this.touchState.touchCount;
    }

    getTouchPosition(): Vector2Like {
        const firstTouch = Array.from(this.touchState.activeTouches.values())[0];
        return firstTouch ? firstTouch.position.canvas : { x: 0, y: 0 };
    }

    isTap(): boolean {
        // Return and consume the tap flag (distance-based detection)
        if (this.tapDetected) {
            this.tapDetected = false; // Reset flag after reading
            return true;
        }
        return false;
    }

    getAllTouches(): TouchPoint[] {
        return Array.from(this.touchState.activeTouches.values());
    }

    getTouchState(): Readonly<TouchState> {
        return { ...this.touchState };
    }

    getPinchDistance(): number {
        return this.currentPinchDistance;
    }

    getPinchDelta(): number {
        return this.pinchDelta;
    }

    isPinching(): boolean {
        return this.touchState.touchCount === 2;
    }

    getTouchCenter(): Vector2Like {
        if (this.touchState.touchCount < 2) {
            return this.getTouchPosition();
        }

        const touches = Array.from(this.touchState.activeTouches.values());
        const touch1 = touches[0];
        const touch2 = touches[1];

        return {
            x: (touch1.position.canvas.x + touch2.position.canvas.x) / 2,
            y: (touch1.position.canvas.y + touch2.position.canvas.y) / 2
        };
    }

    getTouchDelta(): Vector2Like {
        if (!this.touchState.isActive) {
            return { x: 0, y: 0 };
        }

        if (this.touchState.touchCount === 1) {
            const touch = Array.from(this.touchState.activeTouches.values())[0];
            if (!this.lastTouchPosition) {
                this.lastTouchPosition = { ...touch.position.canvas };
                return { x: 0, y: 0 };
            }

            const delta = {
                x: touch.position.canvas.x - this.lastTouchPosition.x,
                y: touch.position.canvas.y - this.lastTouchPosition.y
            };

            this.lastTouchPosition = { ...touch.position.canvas };
            return delta;
        }

        // Two touches - use center delta
        const center = this.getTouchCenter();
        if (!this.lastTouchPosition) {
            this.lastTouchPosition = { ...center };
            return { x: 0, y: 0 };
        }

        const delta = {
            x: center.x - this.lastTouchPosition.x,
            y: center.y - this.lastTouchPosition.y
        };

        this.lastTouchPosition = { ...center };
        return delta;
    }

    // ===== GESTURE/SWIPE METHODS =====

    startSwipe(position: Vector2Like): void {
        this.isActive = true;
        this.swipeStart = { ...position };
        this.swipeStartTime = performance.now();
        this.currentSwipe = null;
        this.swipeDistance = 0;
        this.currentSwipeData = null;
        this.hasStopped = false;
        this.startSwipeLoop();
    }

    updateSwipe(position: Vector2Like): void {
        if (!this.isActive || !this.swipeStart) return;

        this.swipeEnd = { ...position };
        this.hasStopped = false;

        this.clearStopTimer();

        this.stopTimer = window.setTimeout(() => {
            if (!this.hasStopped) {
                this.hasStopped = true;
                this.onMovementStopped();
            }
        }, 100);
    }

    endSwipe(position: Vector2Like): SwipeData | null {
        if (!this.isActive || !this.swipeStart) {
            this.reset();
            return null;
        }

        this.swipeEnd = { ...position };
        this.detectSwipe();

        const swipeData = this.currentSwipeData;

        setTimeout(() => {
            this.reset();
        }, 100);

        this.isActive = false;
        this.stopSwipeLoop();
        this.clearStopTimer();

        return swipeData;
    }

    cancelSwipe(): void {
        this.reset();
        this.stopSwipeLoop();
        this.clearStopTimer();
    }

    reset(): void {
        this.swipeStart = null;
        this.swipeEnd = null;
        this.currentSwipe = null;
        this.swipeDistance = 0;
        this.currentSwipeData = null;
        this.isActive = false;
        this.hasStopped = false;
    }

    getSwipeDirection(): SwipeDirection | null {
        return this.currentSwipe;
    }

    getSwipeDistance(): number {
        return this.swipeDistance;
    }

    getSwipeData(): SwipeData | null {
        return this.currentSwipeData;
    }

    isSwipeDirection(direction: SwipeDirection): boolean {
        return this.currentSwipe === direction;
    }

    setSwipeThreshold(threshold: number): void {
        this.swipeThreshold = Math.max(10, threshold);
    }

    getSwipeThreshold(): number {
        return this.swipeThreshold;
    }

    consumeSwipe(): SwipeData | null {
        const swipe = this.currentSwipeData;
        this.currentSwipe = null;
        this.swipeDistance = 0;
        this.currentSwipeData = null;
        return swipe;
    }

    // ===== UTILITY =====

    clear(): void {
        this.touchState.isActive = false;
        this.touchState.activeTouches.clear();
        this.touchState.touchCount = 0;
        this.lastTouchPosition = null;
        this.reset();
    }

    // ===== PRIVATE METHODS =====

    private calculatePinchDistance(): number {
        if (this.touchState.touchCount < 2) return 0;

        const touches = Array.from(this.touchState.activeTouches.values());
        const touch1 = touches[0];
        const touch2 = touches[1];

        const dx = touch1.position.canvas.x - touch2.position.canvas.x;
        const dy = touch1.position.canvas.y - touch2.position.canvas.y;

        return Math.sqrt(dx * dx + dy * dy);
    }

    private detectSwipe(): void {
        if (!this.swipeStart || !this.swipeEnd) {
            this.currentSwipe = null;
            this.swipeDistance = 0;
            this.currentSwipeData = null;
            return;
        }

        const dx = this.swipeEnd.x - this.swipeStart.x;
        const dy = this.swipeEnd.y - this.swipeStart.y;
        const distance = Math.sqrt(dx * dx + dy * dy);

        this.swipeDistance = distance;

        if (distance < this.swipeThreshold) {
            this.currentSwipe = null;
            this.currentSwipeData = null;
            this.swipeDistance = 0;
            return;
        }

        const angle = Math.atan2(dy, dx);
        let degrees = (angle * 180 / Math.PI + 360) % 360;

        let direction: SwipeDirection;

        if (degrees >= 337.5 || degrees < 22.5) {
            direction = "right";
        } else if (degrees >= 22.5 && degrees < 67.5) {
            direction = "down-right";
        } else if (degrees >= 67.5 && degrees < 112.5) {
            direction = "down";
        } else if (degrees >= 112.5 && degrees < 157.5) {
            direction = "down-left";
        } else if (degrees >= 157.5 && degrees < 202.5) {
            direction = "left";
        } else if (degrees >= 202.5 && degrees < 247.5) {
            direction = "up-left";
        } else if (degrees >= 247.5 && degrees < 292.5) {
            direction = "up";
        } else {
            direction = "up-right";
        }

        this.currentSwipe = direction;

        const duration = performance.now() - this.swipeStartTime;
        this.currentSwipeData = {
            direction,
            distance,
            startPosition: { ...this.swipeStart },
            endPosition: { ...this.swipeEnd },
            duration,
        };
    }

    private startSwipeLoop(): void {
        const loop = () => {
            if (!this.isActive || !this.swipeStart) {
                return;
            }

            this.detectSwipe();
            this.swipeLoopId = requestAnimationFrame(loop);
        };

        this.swipeLoopId = requestAnimationFrame(loop);
    }

    private stopSwipeLoop(): void {
        if (this.swipeLoopId !== null) {
            cancelAnimationFrame(this.swipeLoopId);
            this.swipeLoopId = null;
        }
    }

    private clearStopTimer(): void {
        if (this.stopTimer !== null) {
            clearTimeout(this.stopTimer);
            this.stopTimer = null;
        }
    }

    private onMovementStopped(): void {
        if (this.isActive && this.swipeStart) {
            this.swipeEnd = this.swipeEnd || this.swipeStart;
            this.stopSwipeLoop();
        }
    }
}

